import { useState, useEffect } from "react";
import { useAuth } from "../auth";
import api from "../api";

// Stable, module-level component -- must NOT be defined inside Profile(),
// or React remounts the <input> (and drops focus/characters) on every keystroke.
function Field({label, field, type="text", readOnly=false, editing, data, form, setForm}) {
  return (
    <div>
      <label>{label}</label>
      {editing && !readOnly
        ? <input className="inp" type={type} value={form[field]||""}
            onChange={e=>setForm({...form,[field]:e.target.value})}/>
        : <div style={{padding:"8px 0",fontSize:14,color:data[field]?"var(--ink)":"var(--muted)"}}>
            {data[field]||"—"}
          </div>}
    </div>
  );
}

export default function Profile() {
  const { me } = useAuth();
  const [data,     setData]     = useState(null);
  const [editing,  setEditing]  = useState(false);
  const [form,     setForm]     = useState({});
  const [pw,       setPw]       = useState({new_password:"",confirm:""});
  const [msg,      setMsg]      = useState("");
  const [pwMsg,    setPwMsg]    = useState("");
  const [photo,    setPhoto]    = useState(null);   // current profile photo blob URL
  const [newPhoto, setNewPhoto] = useState(null);   // file to upload
  const [photoMsg, setPhotoMsg] = useState("");

  // Load profile data
  useEffect(() => {
    api.get("/portal/profile/extended")
      .then(d => { setData(d); setForm(d); })
      .catch(()=>{});
  }, []);

  // Load current profile photo
  useEffect(() => {
    fetch("/api/portal/photo", {
      headers: { Authorization: `Bearer ${sessionStorage.getItem("iws_token")}` },
    }).then(r => r.ok ? r.blob() : null)
      .then(b => b && setPhoto(URL.createObjectURL(b)))
      .catch(()=>{});
  }, []);

  const save = async () => {
    try {
      await api.patch("/portal/profile", form);
      setMsg("✓ Saved"); setEditing(false);
      api.get("/portal/profile/extended").then(d => setData(d));
    } catch(e) { setMsg(e.message); }
  };

  const changePw = async () => {
    if (pw.new_password !== pw.confirm) { setPwMsg("Passwords don't match."); return; }
    if (pw.new_password.length < 6)     { setPwMsg("Min 6 characters."); return; }
    try {
      await api.post("/portal/change_password", {new_password: pw.new_password});
      setPwMsg("✓ Password changed"); setPw({new_password:"",confirm:""});
    } catch(e) { setPwMsg(e.message); }
  };

  const uploadPhoto = async () => {
    if (!newPhoto) return;
    setPhotoMsg("Uploading…");
    const fd = new FormData();
    fd.append("photo", newPhoto);
    fd.append("force", "true");
    try {
      // Use own photo endpoint (not admin endpoint) so HR users can also upload
      const res = await fetch("/api/portal/photo", {
        method: "POST",
        headers: { Authorization: `Bearer ${sessionStorage.getItem("iws_token")}` },
        body: fd,
      });
      const r = await res.json();
      if (r.ok) {
        setPhotoMsg("✓ Photo & face enrolled!");
        // Refresh avatar
        setPhoto(URL.createObjectURL(newPhoto));
      } else {
        // Photo saved but face not detected
        setPhotoMsg(`Photo saved. Face: ${r.reason || "not detected"}`);
        setPhoto(URL.createObjectURL(newPhoto));
      }
    } catch(e) { setPhotoMsg(e.message); }
  };

  if (!data) return <div className="loading">Loading…</div>;

  const initials = (n) => (n||"?").split(" ").slice(0,2).map(w=>w[0]).join("").toUpperCase();



  return (
    <div style={{maxWidth:700}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
        <div><h2 className="page-title">Profile</h2><p className="page-sub">Your personal information</p></div>
        {!editing
          ? <button className="btn secondary" onClick={()=>setEditing(true)}>✏ Edit</button>
          : <div style={{display:"flex",gap:8}}>
              <button className="btn" onClick={save}>Save</button>
              <button className="btn secondary" onClick={()=>setEditing(false)}>Cancel</button>
            </div>}
      </div>
      {msg&&<div style={{marginBottom:12,fontSize:13,color:msg.startsWith("✓")?"var(--ok)":"var(--danger)"}}>{msg}</div>}

      {/* Identity card with real photo */}
      <div className="card" style={{marginBottom:16,display:"flex",gap:20,alignItems:"center",flexWrap:"wrap"}}>
        <div style={{width:80,height:80,borderRadius:"50%",overflow:"hidden",flexShrink:0,
          background:"var(--accent)",display:"flex",alignItems:"center",justifyContent:"center",
          color:"#fff",fontSize:28,fontWeight:700}}>
          {photo
            ? <img src={photo} alt="avatar" style={{width:"100%",height:"100%",objectFit:"cover"}}/>
            : initials(data.name)}
        </div>
        <div style={{flex:1}}>
          <div style={{fontSize:22,fontWeight:700}}>{data.name}</div>
          <div style={{color:"var(--muted)",fontSize:13}}>{data.employee_id} · {data.role}</div>
          <div style={{color:"var(--muted)",fontSize:13}}>{data.email}</div>
        </div>
        <span className={`pill ${data.face_enrolled?"green":"orange"}`}>
          {data.face_enrolled?"✓ Face enrolled":"No face"}
        </span>
      </div>

      {/* Photo upload */}
      <div className="card" style={{marginBottom:16}}>
        <h3 style={{fontSize:13,fontWeight:700,textTransform:"uppercase",letterSpacing:".06em",
          color:"var(--muted)",marginBottom:14}}>Profile Photo & Face Enrollment</h3>
        <div style={{display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
          <label style={{cursor:"pointer",display:"inline-flex",alignItems:"center",gap:6,
            background:"var(--soft)",border:"1.5px dashed var(--accent)",borderRadius:8,
            padding:"9px 16px",fontSize:13,color:"var(--accent)"}}>
            📷 Choose Photo
            <input type="file" accept="image/*" style={{display:"none"}}
              onChange={e=>setNewPhoto(e.target.files[0])}/>
          </label>
          {newPhoto && (
            <img src={URL.createObjectURL(newPhoto)} alt="preview"
              style={{width:52,height:52,borderRadius:"50%",objectFit:"cover",
                border:"2px solid var(--accent)"}}/>
          )}
          <button className="btn" disabled={!newPhoto} onClick={uploadPhoto}>Upload & Enroll</button>
          {photoMsg && (
            <span style={{fontSize:13,
              color:photoMsg.startsWith("✓")?"var(--ok)":photoMsg.includes("saved")?"#D97706":"var(--danger)"}}>
              {photoMsg}
            </span>
          )}
        </div>
        <p style={{fontSize:12,color:"var(--muted)",marginTop:8}}>
          Use a clear front-facing photo with good lighting for face recognition.
        </p>
      </div>

      {/* Personal info */}
      <div className="card" style={{marginBottom:16}}>
        <h3 style={{fontSize:13,fontWeight:700,textTransform:"uppercase",letterSpacing:".06em",color:"var(--muted)",marginBottom:14}}>Personal Information</h3>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
          <Field editing={editing} data={data} form={form} setForm={setForm} label="First Name"    field="first_name"/>
          <Field editing={editing} data={data} form={form} setForm={setForm} label="Last Name"     field="last_name"/>
          <Field editing={editing} data={data} form={form} setForm={setForm} label="Email"         field="email" readOnly/>
          <Field editing={editing} data={data} form={form} setForm={setForm} label="Personal Email" field="personal_email"/>
          <Field editing={editing} data={data} form={form} setForm={setForm} label="Mobile"        field="mobile"/>
          <Field editing={editing} data={data} form={form} setForm={setForm} label="Date of Birth" field="date_of_birth" type="date"/>
          <Field editing={editing} data={data} form={form} setForm={setForm} label="Gender"        field="gender" readOnly/>
          <Field editing={editing} data={data} form={form} setForm={setForm} label="Marital Status"field="marital_status"/>
          <Field editing={editing} data={data} form={form} setForm={setForm} label="Location"      field="location" readOnly/>
        </div>
        <div style={{marginTop:14}}><Field editing={editing} data={data} form={form} setForm={setForm} label="Address" field="address"/></div>
      </div>

      {/* Work info */}
      <div className="card" style={{marginBottom:16}}>
        <h3 style={{fontSize:13,fontWeight:700,textTransform:"uppercase",letterSpacing:".06em",color:"var(--muted)",marginBottom:14}}>Work Information</h3>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
          <Field editing={editing} data={data} form={form} setForm={setForm} label="Department"      field="department"     readOnly/>
          <Field editing={editing} data={data} form={form} setForm={setForm} label="Designation"     field="designation"    readOnly/>
          <Field editing={editing} data={data} form={form} setForm={setForm} label="Date of Joining" field="date_of_joining"readOnly/>
          <Field editing={editing} data={data} form={form} setForm={setForm} label="Reporting To"    field="reporting_to_name" readOnly/>
        </div>
      </div>

      {/* Change password */}
      <div className="card">
        <h3 style={{fontSize:13,fontWeight:700,textTransform:"uppercase",letterSpacing:".06em",color:"var(--muted)",marginBottom:14}}>Change Password</h3>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,maxWidth:420}}>
          <div><label>New Password</label>
            <input className="inp" type="password" value={pw.new_password}
              onChange={e=>setPw({...pw,new_password:e.target.value})} placeholder="Min 6 characters"/></div>
          <div><label>Confirm Password</label>
            <input className="inp" type="password" value={pw.confirm}
              onChange={e=>setPw({...pw,confirm:e.target.value})}/></div>
        </div>
        <div style={{marginTop:12,display:"flex",gap:10,alignItems:"center"}}>
          <button className="btn" onClick={changePw} disabled={!pw.new_password||!pw.confirm} style={{opacity:(!pw.new_password||!pw.confirm)?0.5:1}}>Update Password</button>
          {pwMsg&&<span style={{fontSize:13,color:pwMsg.startsWith("✓")?"var(--ok)":"var(--danger)"}}>{pwMsg}</span>}
        </div>
      </div>
    </div>
  );
}
